/**
 * Hook que coleta o contexto da tela atual para o fluxo "Reportar esta tela"
 * (Report-This-Screen). Usado por useReportScreen ao montar o body do
 * POST /v1/chamados/from-screen.
 *
 * Spec: §9.2.5 do CHAMADOS_VISIBILIDADE_SPEC.md
 * Gaps cobertos: G-N8 (componente da rota), G-N9 (breadcrumbs + console).
 *
 * Dados coletados:
 *  - route_path: location.pathname
 *  - route_label / component: via routeRegistry (TASK-2464)
 *  - user_agent (truncado a 500 chars)
 *  - app_version: __APP_VERSION__ (definido em vite-env.d.ts)
 *  - selected_tenant_id + user_id: do useAuthStore
 *  - breadcrumbs: ultimas 10 rotas visitadas (via screenContext util)
 *  - console_errors: ultimos 5 erros capturados (via screenContext util)
 *  - last_error: window.__lastScreenError (se houver — capturado pelo
 *    ErrorBoundary global)
 */

import { useLocation } from 'react-router-dom'
import { detectComponentForRoute } from '@/utils/routeRegistry'
import {
  getRecentBreadcrumbs,
  getRecentConsoleErrors,
} from '@/utils/screenContext'
import { useAuthStore } from '@/store/auth'
import { routeHeader } from '@/utils/routeHeader'

/**
 * Nome humano da tela, para o metadado de chamado.
 *
 * NUNCA o caminho da URL: `route_path` ja o carrega, e rotulo com barra e sem acento e a mesma
 * classe de defeito que a trilha tinha ate 2026-08-13 ("Home / admin/organizacoes"). O rotulo
 * deve ser o nome que a pessoa estava VENDO no cabecalho — e e o routeHeader que decide isso.
 */
export function rotuloDaTela(pathname: string): string {
  return routeHeader(pathname).titulo
}

const USER_AGENT_MAX = 500

export interface ScreenContextLastError {
  message: string
  component_stack: string
  error_id: string
}

export interface ScreenContext {
  route_path: string
  route_label: string
  component: string | undefined
  user_agent: string
  app_version: string
  selected_tenant_id: number | null
  user_id: number | null
  breadcrumbs: string[]
  console_errors: string[]
  last_error: ScreenContextLastError | null
  viewport: { width: number; height: number }
}

export function useScreenContext(): ScreenContext {
  const location = useLocation()
  const route = detectComponentForRoute(location.pathname)
  const user = useAuthStore((s) => s.user)
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId)

  const userAgent =
    typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, USER_AGENT_MAX) : ''

  const viewport =
    typeof window !== 'undefined'
      ? { width: window.innerWidth, height: window.innerHeight }
      : { width: 0, height: 0 }

  let lastError: ScreenContextLastError | null = null
  if (typeof window !== 'undefined' && window.__lastScreenError) {
    const w = window.__lastScreenError
    lastError = {
      message: w.error?.message ?? '',
      component_stack: w.errorInfo?.componentStack ?? '',
      error_id: w.errorId ?? '',
    }
  }

  return {
    route_path: location.pathname,
    route_label: route?.label ?? rotuloDaTela(location.pathname),
    component: route?.component,
    user_agent: userAgent,
    app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
    selected_tenant_id: selectedTenantId ?? null,
    user_id: user?.id ?? null,
    breadcrumbs: getRecentBreadcrumbs(),
    console_errors: getRecentConsoleErrors(),
    last_error: lastError,
    viewport,
  }
}
